import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { 
  PhonebookContact, 
  ContactCategory, 
  ImportPreviewContact, 
  ImportResult, 
  ColumnMapping,
  UserProfileUpdateRequest 
} from '@openmirror/shared';
import { useActualApi } from '../hooks/useActualApi';
import { useAuth } from './AuthContext';
import { useProfile } from './ProfileContext';
import { validateContact, sanitizePhoneNumber, validateAndSanitizePhoneNumber } from '../utils/contactValidation';
import { parseFile } from '../utils/fileParser';
import { debounce } from '../utils';
import { 
  withRetry, 
  createNetworkError, 
  createFileError, 
  createValidationError,
  NetworkErrorCode,
  FileErrorCode,
  ValidationErrorCode,
  PhonebookError,
  ErrorType
} from '../utils/errorHandling';
import { useSuccessFeedback } from '../hooks/useSuccessFeedback';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';

interface ContactContextState {
  contacts: PhonebookContact[];
  categories: ContactCategory[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  debouncedSearchTerm: string;
  selectedCategory: string | null;
  filteredContacts: PhonebookContact[];
  
  // Import/Export states
  isImporting: boolean;
  importProgress: number;
  isExporting: boolean;
  
  // Category management states
  isCategoryLoading: boolean;
  categoryError: string | null;
}

interface ContactContextActions {
  // Contact CRUD operations
  loadContacts: () => Promise<void>;
  addContact: (contact: Omit<PhonebookContact, 'id' | 'createdAt' | 'updatedAt'>) => Promise<PhonebookContact>;
  updateContact: (id: string, updates: Partial<Omit<PhonebookContact, 'id' | 'createdAt'>>) => Promise<PhonebookContact>;
  deleteContact: (id: string) => Promise<void>;
  
  // Category CRUD operations
  loadCategories: () => Promise<void>;
  createCategory: (name: string) => Promise<ContactCategory>;
  updateCategory: (id: string, name: string) => Promise<ContactCategory>;
  deleteCategory: (id: string) => Promise<void>;
  
  // Import/Export operations
  importContacts: (contacts: ImportPreviewContact[], categoryId: string) => Promise<ImportResult>;
  exportContacts: (format: 'csv' | 'excel', filtered?: boolean) => Promise<void>;
  parseImportFile: (file: File) => Promise<ImportPreviewContact[]>;
  
  // Search and filter operations
  setSearchTerm: (term: string) => void;
  setSelectedCategory: (categoryId: string | null) => void;
  clearFilters: () => void;
  isSearching: boolean;
  
  // Utility operations
  refreshData: () => Promise<void>;
  clearError: () => void;
}

type ContactContextType = ContactContextState & ContactContextActions;

const ContactContext = createContext<ContactContextType | undefined>(undefined);

export const ContactProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { idToken } = useAuth();
  const { profile, updateProfile } = useProfile();
  const successFeedback = useSuccessFeedback();
  const { 
    updateOperatorProfile,
    // Enhanced phonebook API methods
    getContactCategories,
    createContactCategory,
    updateContactCategory,
    deleteContactCategory,
    getPhonebookContacts,
    createPhonebookContact,
    updatePhonebookContact,
    deletePhonebookContact,
    bulkImportContacts,
    exportContacts: exportContactsApi
  } = useActualApi();
  
  // Ref to track if we're currently loading to prevent multiple simultaneous calls
  const isLoadingRef = useRef(false);
  const lastLoadTimeRef = useRef<number>(0);
  
  // State management
  const [contacts, setContacts] = useState<PhonebookContact[]>([]);
  const [categories, setCategories] = useState<ContactCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  // Enhanced debounced search with loading states
  const {
    searchTerm,
    debouncedSearchTerm,
    isSearching,
    setSearchTerm,
    clearSearch
  } = useDebouncedSearch('', {
    delay: 300,
    minLength: 1,
    onSearchStart: () => {
      // Optional: Add search start analytics or loading states
    },
    onSearchComplete: () => {
      // Optional: Add search complete analytics
    }
  });
  
  // Import/Export states
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  
  // Category management states
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);

  // Search state is now managed by useDebouncedSearch hook

  // Computed filtered contacts
  const filteredContacts = useMemo(() => {
    let filtered = contacts;
    
    // Apply search filter using debounced search term
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(contact => 
        contact.name.toLowerCase().includes(searchLower) ||
        contact.number.includes(debouncedSearchTerm.trim())
      );
    }
    
    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(contact => contact.category === selectedCategory);
    }
    
    return filtered;
  }, [contacts, debouncedSearchTerm, selectedCategory]);

  // Generate unique ID for new contacts/categories
  const generateId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Ensure "General" category exists
  const ensureGeneralCategory = useCallback(async (): Promise<ContactCategory> => {
    const generalCategory = categories.find(cat => cat.isDefault);
    if (generalCategory) {
      return generalCategory;
    }

    const newGeneralCategory: ContactCategory = {
      id: generateId(),
      name: 'General',
      contactCount: 0,
      isDefault: true,
      createdAt: new Date().toISOString()
    };

    setCategories(prev => [...prev, newGeneralCategory]);
    return newGeneralCategory;
  }, [categories, generateId]);

  // Load contacts from API
  const loadContacts = useCallback(async () => {
    if (!idToken) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await withRetry(async () => {
        const result = await getPhonebookContacts();
        if (!result.success) {
          throw createNetworkError(NetworkErrorCode.SERVER_ERROR, result.error);
        }
        return result;
      });

      if (response.contacts) {
        setContacts(response.contacts);
      } else {
        // Fallback to profile data if API returns no contacts
        const profileContacts = profile?.phonebook || [];
        setContacts(profileContacts);
      }
    } catch (err: any) {
      console.error('Error loading contacts:', err);
      
      // Try fallback to profile data
      try {
        const profileContacts = profile?.phonebook || [];
        setContacts(profileContacts);
        
        // Only show error if fallback also fails or no profile data
        if (profileContacts.length === 0) {
          if (err instanceof PhonebookError) {
            setError(err.userMessage);
          } else {
            const networkError = createNetworkError(NetworkErrorCode.CONNECTION_FAILED, err.message);
            setError(networkError.userMessage);
          }
        }
      } catch (fallbackErr) {
        const networkError = createNetworkError(NetworkErrorCode.CONNECTION_FAILED, 'Failed to load contacts from all sources');
        setError(networkError.userMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [idToken, profile, getPhonebookContacts]);

  // Load categories from API
  const loadCategories = useCallback(async () => {
    if (!idToken) return;
    
    setIsCategoryLoading(true);
    setCategoryError(null);
    
    try {
      const response = await withRetry(async () => {
        const result = await getContactCategories();
        if (!result.success) {
          throw createNetworkError(NetworkErrorCode.SERVER_ERROR, result.error);
        }
        return result;
      });

      if (response.categories) {
        setCategories(response.categories);
        
        // Ensure General category exists
        if (!response.categories.find(cat => cat.isDefault)) {
          await ensureGeneralCategory();
        }
      } else {
        // Fallback to profile data if API returns no categories
        const profileCategories = profile?.contactCategories || [];
        setCategories(profileCategories);
        
        if (!profileCategories.find(cat => cat.isDefault)) {
          await ensureGeneralCategory();
        }
      }
    } catch (err: any) {
      console.error('Error loading categories:', err);
      
      // Try fallback to profile data
      try {
        const profileCategories = profile?.contactCategories || [];
        setCategories(profileCategories);
        
        if (!profileCategories.find(cat => cat.isDefault)) {
          await ensureGeneralCategory();
        }
        
        // Only show error if fallback also fails or no profile data
        if (profileCategories.length === 0) {
          if (err instanceof PhonebookError) {
            setCategoryError(err.userMessage);
          } else {
            const networkError = createNetworkError(NetworkErrorCode.CONNECTION_FAILED, err.message);
            setCategoryError(networkError.userMessage);
          }
        }
      } catch (fallbackErr) {
        const networkError = createNetworkError(NetworkErrorCode.CONNECTION_FAILED, 'Failed to load categories from all sources');
        setCategoryError(networkError.userMessage);
      }
    } finally {
      setIsCategoryLoading(false);
    }
  }, [idToken, profile, getContactCategories, ensureGeneralCategory]);

  // Update contact counts for categories
  const updateCategoryCounts = () => {
    setCategories(prev => prev.map(category => ({
      ...category,
      contactCount: contacts.filter(contact => contact.category === category.name).length
    })));
  };

  // Save contacts and categories to profile
  const saveToProfile = useCallback(async (updatedContacts?: PhonebookContact[], updatedCategories?: ContactCategory[]) => {
    if (!profile) return;
    
    const contactsToSave = updatedContacts || contacts;
    const categoriesToSave = updatedCategories || categories;
    
    const updateData: UserProfileUpdateRequest = {
      phonebook: contactsToSave,
      contactCategories: categoriesToSave
    };
    
    await updateProfile(updateData);
  }, [profile, contacts, categories, updateProfile]);

  // Add new contact
  const addContact = useCallback(async (contactData: Omit<PhonebookContact, 'id' | 'createdAt' | 'updatedAt'>): Promise<PhonebookContact> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Validate contact data
      const validation = validateContact(contactData, categories);
      if (!validation.isValid) {
        const errorMessages = Object.values(validation.errors).filter(Boolean);
        throw createValidationError(ValidationErrorCode.REQUIRED_FIELD, errorMessages.join(', '));
      }
      
      // Sanitize phone number
      const phoneValidation = validateAndSanitizePhoneNumber(contactData.number);
      if (!phoneValidation.isValid || !phoneValidation.sanitizedNumber) {
        throw createValidationError(ValidationErrorCode.INVALID_PHONE, phoneValidation.error);
      }
      const sanitizedNumber = phoneValidation.sanitizedNumber;
      
      // Check for duplicates
      const existingContact = contacts.find(c => c.number === sanitizedNumber);
      if (existingContact) {
        throw createValidationError(ValidationErrorCode.DUPLICATE_CONTACT, `Contact "${existingContact.name}" already has this number`);
      }
      
      // Ensure category exists
      if (!categories.find(cat => cat.name === contactData.category)) {
        await ensureGeneralCategory();
      }
      
      // Create contact via API with retry
      const createPayload = {
        name: contactData.name,
        number: sanitizedNumber,
        category: contactData.category
      };
      
      const response = await withRetry(async () => {
        const result = await createPhonebookContact(createPayload);
        if (!result.success) {
          throw createNetworkError(NetworkErrorCode.SERVER_ERROR, result.error);
        }
        return result;
      });
      
      if (response.contact) {
        // Update local state with API response
        setContacts(prev => [...prev, response.contact!]);
        successFeedback.showContactAdded(response.contact.name);
        return response.contact;
      } else {
        // Fallback to local creation if API doesn't return contact
        const newContact: PhonebookContact = {
          ...contactData,
          id: generateId(),
          number: sanitizedNumber,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        const updatedContacts = [...contacts, newContact];
        setContacts(updatedContacts);
        
        // Try to save to profile as fallback
        await saveToProfile(updatedContacts);
        
        successFeedback.showContactAdded(newContact.name);
        return newContact;
      }
    } catch (err: any) {
      console.error('Error adding contact:', err);
      
      if (err instanceof PhonebookError) {
        setError(err.userMessage);
        throw err;
      } else {
        const networkError = createNetworkError(NetworkErrorCode.CONNECTION_FAILED, err.message);
        setError(networkError.userMessage);
        throw networkError;
      }
    } finally {
      setIsLoading(false);
    }
  }, [contacts, categories, generateId, ensureGeneralCategory, createPhonebookContact, saveToProfile]);

  // Update existing contact
  const updateContact = useCallback(async (id: string, updates: Partial<Omit<PhonebookContact, 'id' | 'createdAt'>>): Promise<PhonebookContact> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const existingContact = contacts.find(c => c.id === id);
      if (!existingContact) {
        throw new Error('Contact not found');
      }
      
      // Validate updated data if name or number is being changed
      if (updates.name !== undefined || updates.number !== undefined) {
        const contactToValidate = {
          name: updates.name !== undefined ? updates.name : existingContact.name,
          number: updates.number !== undefined ? updates.number : existingContact.number,
          category: updates.category !== undefined ? updates.category : existingContact.category
        };
        
        const validation = validateContact(contactToValidate, categories);
        if (!validation.isValid) {
          const errorMessages = Object.values(validation.errors).filter(Boolean);
          throw new Error(errorMessages.join(', '));
        }
        
        // Sanitize phone number if it's being updated
        if (updates.number !== undefined) {
          const phoneValidation = validateAndSanitizePhoneNumber(updates.number);
          if (!phoneValidation.isValid || !phoneValidation.sanitizedNumber) {
            throw new Error(phoneValidation.error || 'Invalid phone number');
          }
          updates.number = phoneValidation.sanitizedNumber;
          
          // Check for duplicates (excluding current contact)
          const existingWithNumber = contacts.find(c => c.id !== id && c.number === updates.number);
          if (existingWithNumber) {
            throw new Error('A contact with this phone number already exists');
          }
        }
      }
      
      // Update contact via API
      const updatePayload = {
        ...(updates.name !== undefined && { name: updates.name }),
        ...(updates.number !== undefined && { number: updates.number }),
        ...(updates.category !== undefined && { category: updates.category })
      };
      
      const response = await updatePhonebookContact(id, updatePayload);
      
      if (response.success && response.contact) {
        // Update local state with API response
        setContacts(prev => prev.map(c => c.id === id ? response.contact! : c));
        successFeedback.showContactUpdated(response.contact.name);
        return response.contact;
      } else {
        // Fallback to local update if API fails
        const updatedContact: PhonebookContact = {
          ...existingContact,
          ...updates,
          updatedAt: new Date().toISOString()
        };
        
        const updatedContacts = contacts.map(c => c.id === id ? updatedContact : c);
        setContacts(updatedContacts);
        
        // Try to save to profile as fallback
        await saveToProfile(updatedContacts);
        
        successFeedback.showContactUpdated(updatedContact.name);
        return updatedContact;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update contact');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [contacts, categories, updatePhonebookContact, saveToProfile]);

  // Delete contact
  const deleteContact = useCallback(async (id: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    // Find contact name for success message
    const contactToDelete = contacts.find(c => c.id === id);
    const contactName = contactToDelete?.name || 'Contact';
    
    try {
      // Delete contact via API with retry
      const response = await withRetry(async () => {
        const result = await deletePhonebookContact(id);
        if (!result.success) {
          throw createNetworkError(NetworkErrorCode.SERVER_ERROR, result.error);
        }
        return result;
      });
      
      // Update local state
      setContacts(prev => prev.filter(c => c.id !== id));
      successFeedback.showContactDeleted(contactName);
    } catch (err: any) {
      console.error('Error deleting contact:', err);
      
      // Try fallback to local deletion
      try {
        const updatedContacts = contacts.filter(c => c.id !== id);
        setContacts(updatedContacts);
        
        // Try to save to profile as fallback
        await saveToProfile(updatedContacts);
        
        successFeedback.showContactDeleted(contactName);
      } catch (fallbackErr) {
        if (err instanceof PhonebookError) {
          setError(err.userMessage);
          throw err;
        } else {
          const networkError = createNetworkError(NetworkErrorCode.CONNECTION_FAILED, err.message);
          setError(networkError.userMessage);
          throw networkError;
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [contacts, deletePhonebookContact, saveToProfile, successFeedback]);

  // Create new category
  const createCategory = useCallback(async (name: string): Promise<ContactCategory> => {
    setIsCategoryLoading(true);
    setCategoryError(null);
    
    try {
      // Validate category name
      if (!name.trim()) {
        throw new Error('Category name cannot be empty');
      }
      
      // Check for duplicates
      if (categories.find(cat => cat.name.toLowerCase() === name.toLowerCase().trim())) {
        throw new Error('A category with this name already exists');
      }
      
      // Create category via API
      const createPayload = { name: name.trim() };
      const response = await createContactCategory(createPayload);
      
      if (response.success && response.category) {
        // Update local state with API response
        setCategories(prev => [...prev, response.category!]);
        return response.category;
      } else {
        // Fallback to local creation if API fails
        const newCategory: ContactCategory = {
          id: generateId(),
          name: name.trim(),
          contactCount: 0,
          isDefault: false,
          createdAt: new Date().toISOString()
        };
        
        const updatedCategories = [...categories, newCategory];
        setCategories(updatedCategories);
        
        // Try to save to profile as fallback
        await saveToProfile(undefined, updatedCategories);
        
        return newCategory;
      }
    } catch (err: any) {
      setCategoryError(err.message || 'Failed to create category');
      throw err;
    } finally {
      setIsCategoryLoading(false);
    }
  }, [categories, generateId, createContactCategory, saveToProfile]);

  // Update category name
  const updateCategory = useCallback(async (id: string, name: string): Promise<ContactCategory> => {
    setIsCategoryLoading(true);
    setCategoryError(null);
    
    try {
      const existingCategory = categories.find(cat => cat.id === id);
      if (!existingCategory) {
        throw new Error('Category not found');
      }
      
      // Validate new name
      if (!name.trim()) {
        throw new Error('Category name cannot be empty');
      }
      
      // Check for duplicates (excluding current category)
      if (categories.find(cat => cat.id !== id && cat.name.toLowerCase() === name.toLowerCase().trim())) {
        throw new Error('A category with this name already exists');
      }
      
      const oldName = existingCategory.name;
      const newName = name.trim();
      
      // Update category via API
      const updatePayload = { name: newName };
      const response = await updateContactCategory(id, updatePayload);
      
      if (response.success && response.category) {
        // Update local state with API response
        setCategories(prev => prev.map(cat => cat.id === id ? response.category! : cat));
        
        // Update all contacts that use this category
        const updatedContacts = contacts.map(contact => 
          contact.category === oldName 
            ? { ...contact, category: newName, updatedAt: new Date().toISOString() }
            : contact
        );
        setContacts(updatedContacts);
        
        return response.category;
      } else {
        // Fallback to local update if API fails
        const updatedCategory = { ...existingCategory, name: newName };
        const updatedCategories = categories.map(cat => cat.id === id ? updatedCategory : cat);
        setCategories(updatedCategories);
        
        // Update all contacts that use this category
        const updatedContacts = contacts.map(contact => 
          contact.category === oldName 
            ? { ...contact, category: newName, updatedAt: new Date().toISOString() }
            : contact
        );
        setContacts(updatedContacts);
        
        // Try to save to profile as fallback
        await saveToProfile(updatedContacts, updatedCategories);
        
        return updatedCategory;
      }
    } catch (err: any) {
      setCategoryError(err.message || 'Failed to update category');
      throw err;
    } finally {
      setIsCategoryLoading(false);
    }
  }, [categories, contacts, updateContactCategory, saveToProfile]);

  // Delete category (move contacts to General)
  const deleteCategory = useCallback(async (id: string): Promise<void> => {
    setIsCategoryLoading(true);
    setCategoryError(null);
    
    try {
      const categoryToDelete = categories.find(cat => cat.id === id);
      if (!categoryToDelete) {
        throw new Error('Category not found');
      }
      
      // Prevent deletion of General category
      if (categoryToDelete.isDefault) {
        throw new Error('Cannot delete the General category');
      }
      
      // Ensure General category exists
      const generalCategory = await ensureGeneralCategory();
      
      // Delete category via API
      const response = await deleteContactCategory(id);
      
      if (response.success) {
        // Update local state
        setCategories(prev => prev.filter(cat => cat.id !== id));
        
        // Move all contacts from deleted category to General
        const updatedContacts = contacts.map(contact => 
          contact.category === categoryToDelete.name
            ? { ...contact, category: generalCategory.name, updatedAt: new Date().toISOString() }
            : contact
        );
        setContacts(updatedContacts);
      } else {
        // Fallback to local deletion if API fails
        // Move all contacts from deleted category to General
        const updatedContacts = contacts.map(contact => 
          contact.category === categoryToDelete.name
            ? { ...contact, category: generalCategory.name, updatedAt: new Date().toISOString() }
            : contact
        );
        setContacts(updatedContacts);
        
        // Remove category
        const updatedCategories = categories.filter(cat => cat.id !== id);
        setCategories(updatedCategories);
        
        // Try to save to profile as fallback
        await saveToProfile(updatedContacts, updatedCategories);
      }
    } catch (err: any) {
      setCategoryError(err.message || 'Failed to delete category');
      throw err;
    } finally {
      setIsCategoryLoading(false);
    }
  }, [categories, contacts, ensureGeneralCategory, deleteContactCategory, saveToProfile]);

  // Parse import file
  const parseImportFile = useCallback(async (file: File): Promise<ImportPreviewContact[]> => {
    setError(null);
    
    try {
      // Validate file before parsing
      if (!file) {
        throw createFileError(FileErrorCode.EMPTY_FILE, 'No file provided');
      }
      
      if (file.size === 0) {
        throw createFileError(FileErrorCode.EMPTY_FILE, 'File is empty');
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        throw createFileError(FileErrorCode.FILE_TOO_LARGE, `File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      }
      
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      const fileExtension = file.name.toLowerCase().split('.').pop();
      const allowedExtensions = ['csv', 'xls', 'xlsx'];
      
      if (!allowedExtensions.includes(fileExtension || '') && !allowedTypes.includes(file.type)) {
        throw createFileError(FileErrorCode.INVALID_FORMAT, `File type: ${file.type || 'unknown'}, Extension: ${fileExtension || 'none'}`);
      }
      
      const parseResult = await parseFile(file);
      
      if (!parseResult.success || !parseResult.data) {
        throw createFileError(FileErrorCode.PARSING_FAILED, parseResult.error);
      }
      
      if (!parseResult.data.rows || parseResult.data.rows.length === 0) {
        throw createFileError(FileErrorCode.EMPTY_FILE, 'No data rows found in file');
      }
      
      // Convert parsed data to ImportPreviewContact format
      const previewContacts: ImportPreviewContact[] = parseResult.data.rows.map((row, index) => {
        // Assume first column is name, second is phone number
        // This is a basic implementation - in a real app, you'd have column mapping
        const name = String(row[0] || '').trim();
        const number = String(row[1] || '').trim();
        
        const contactToValidate = { name, number, category: 'General' };
        const validation = validateContact(contactToValidate, categories);
        
        return {
          name,
          number: number ? sanitizePhoneNumber(number) || number : number,
          isValid: validation.isValid,
          validationErrors: Object.values(validation.errors).filter(Boolean),
          originalRowIndex: index
        };
      });
      
      return previewContacts;
    } catch (err: any) {
      console.error('Error parsing import file:', err);
      
      if (err instanceof PhonebookError) {
        setError(err.userMessage);
        throw err;
      } else {
        const fileError = createFileError(FileErrorCode.PARSING_FAILED, err.message);
        setError(fileError.userMessage);
        throw fileError;
      }
    }
  }, [categories]);

  // Import contacts
  const importContacts = useCallback(async (contactsToImport: ImportPreviewContact[], categoryId: string): Promise<ImportResult> => {
    setIsImporting(true);
    setImportProgress(0);
    setError(null);
    
    try {
      // Find category name
      const category = categories.find(cat => cat.id === categoryId);
      const categoryName = category?.name || 'General';
      
      // Prepare contacts for bulk import
      const validContacts = contactsToImport.filter(contact => contact.isValid);
      const contactsForImport = validContacts.map(contact => ({
        name: contact.name,
        number: contact.number,
        category: categoryName
      }));
      
      if (contactsForImport.length === 0) {
        return {
          successful: 0,
          failed: 0,
          duplicates: 0,
          errors: ['No valid contacts to import']
        };
      }
      
      // Use bulk import API
      const bulkImportPayload = {
        contacts: contactsForImport,
        duplicateHandling: 'skip' as const
      };
      
      const response = await bulkImportContacts(bulkImportPayload, (progress) => {
        setImportProgress(progress);
      });
      
      if (response.success && response.result) {
        // Refresh contacts from API to get the latest data
        await loadContacts();
        return response.result;
      } else {
        // Fallback to local import if API fails
        let successful = 0;
        let failed = 0;
        let duplicates = 0;
        const errors: string[] = [];
        
        const totalContacts = validContacts.length;
        
        for (let i = 0; i < validContacts.length; i++) {
          const contact = validContacts[i];
          
          try {
            // Check for existing contact
            const existingContact = contacts.find(c => c.number === contact.number);
            if (existingContact) {
              duplicates++;
              continue;
            }
            
            // Create new contact
            const newContact: PhonebookContact = {
              id: generateId(),
              name: contact.name,
              number: contact.number,
              category: categoryName,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
            
            setContacts(prev => [...prev, newContact]);
            successful++;
          } catch (err: any) {
            failed++;
            errors.push(`Row ${contact.originalRowIndex + 1}: ${err.message}`);
          }
          
          // Update progress
          setImportProgress(Math.round(((i + 1) / totalContacts) * 100));
        }
        
        // Save all contacts at once
        await saveToProfile();
        
        return {
          successful,
          failed,
          duplicates,
          errors
        };
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import contacts');
      throw err;
    } finally {
      setIsImporting(false);
      setImportProgress(0);
    }
  }, [categories, contacts, generateId, bulkImportContacts, loadContacts, saveToProfile]);

  // Export contacts
  const exportContacts = useCallback(async (format: 'csv' | 'excel', filtered: boolean = false): Promise<void> => {
    setIsExporting(true);
    setError(null);
    
    try {
      const contactsToExport = filtered ? filteredContacts : contacts;
      
      if (contactsToExport.length === 0) {
        throw new Error('No contacts to export');
      }
      
      // Try API export first
      try {
        const exportPayload = {
          format,
          ...(filtered && selectedCategory && { categoryFilter: selectedCategory }),
          ...(filtered && searchTerm && { searchFilter: searchTerm })
        };
        
        const response = await exportContactsApi(exportPayload);
        
        if (response.success && response.downloadUrl) {
          // Download file from API response
          const link = document.createElement('a');
          link.href = response.downloadUrl;
          link.download = response.filename || `contacts_${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }
      } catch (apiError) {
        console.warn('API export failed, falling back to client-side export:', apiError);
      }
      
      // Fallback to client-side export
      const { exportContacts: exportUtil } = await import('../utils/contactExport');
      
      exportUtil(contactsToExport, {
        format,
        includeHeaders: true
      });
      
    } catch (err: any) {
      setError(err.message || 'Failed to export contacts');
      throw err;
    } finally {
      setIsExporting(false);
    }
  }, [contacts, filteredContacts, selectedCategory, searchTerm, exportContactsApi]);

  // Search and filter operations - now handled by useDebouncedSearch hook
  const handleSetSearchTerm = setSearchTerm;

  const handleSetSelectedCategory = useCallback((categoryId: string | null) => {
    setSelectedCategory(categoryId);
  }, []);

  const clearFilters = useCallback(() => {
    clearSearch();
    setSelectedCategory(null);
  }, [clearSearch]);

  // Utility operations
  const refreshData = useCallback(async () => {
    const now = Date.now();
    
    // Prevent multiple calls within 2 seconds (handles StrictMode and rapid re-renders)
    if (!idToken || isLoadingRef.current || (now - lastLoadTimeRef.current) < 2000) {
      console.log('ContactContext: Skipping refreshData - no token, already loading, or too recent');
      return;
    }
    
    console.log('ContactContext: Starting refreshData');
    isLoadingRef.current = true;
    lastLoadTimeRef.current = now;
    
    try {
      // Load both contacts and categories
      await Promise.all([
        loadContacts(),
        loadCategories()
      ]);
      console.log('ContactContext: Completed refreshData');
    } finally {
      isLoadingRef.current = false;
    }
  }, [idToken]); // Simplified dependencies

  const clearError = useCallback(() => {
    setError(null);
    setCategoryError(null);
  }, []);

  // Load data when authenticated (only once per token, handles StrictMode)
  useEffect(() => {
    if (idToken) {
      console.log('ContactContext: Token available, loading data');
      // Use a timeout to handle React StrictMode double-invocation
      const timeoutId = setTimeout(() => {
        refreshData();
      }, 50);
      
      return () => clearTimeout(timeoutId);
    } else {
      console.log('ContactContext: No token, clearing data');
      // Clear data when not authenticated
      setContacts([]);
      setCategories([]);
    }
  }, [idToken]); // Only depend on idToken

  // Update category counts when contacts change
  useEffect(() => {
    updateCategoryCounts();
  }, [contacts]); // Use contacts directly instead of updateCategoryCounts function

  // Context value
  const contextValue: ContactContextType = {
    // State
    contacts,
    categories,
    isLoading,
    error,
    searchTerm,
    debouncedSearchTerm,
    selectedCategory,
    filteredContacts,
    isSearching,
    isImporting,
    importProgress,
    isExporting,
    isCategoryLoading,
    categoryError,
    
    // Actions
    loadContacts,
    addContact,
    updateContact,
    deleteContact,
    loadCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    importContacts,
    exportContacts,
    parseImportFile,
    setSearchTerm: handleSetSearchTerm,
    setSelectedCategory: handleSetSelectedCategory,
    clearFilters,
    refreshData,
    clearError
  };

  return (
    <ContactContext.Provider value={contextValue}>
      {children}
    </ContactContext.Provider>
  );
};

export const useContacts = (): ContactContextType => {
  const context = useContext(ContactContext);
  if (context === undefined) {
    throw new Error('useContacts must be used within a ContactProvider');
  }
  return context;
};